import { AlertTriangle, ArrowLeft, Mail, ShieldCheck } from "lucide-react";
import type { PublicConfig } from "../types";

type LegalKind = "privacy" | "termini" | "cookie" | "regole" | "assistenza";

const seller = (config: PublicConfig) => ({
  name: config.legal.companyName || "[INSERIRE RAGIONE SOCIALE]",
  address: config.legal.address || "[INSERIRE SEDE LEGALE]",
  vat: config.legal.vatId || "[INSERIRE P.IVA / C.F.]",
});

function DraftWarning({ ready }: { ready: boolean }) {
  if (ready) return null;
  return (
    <div className="legal-warning">
      <AlertTriangle />
      <p>
        <strong>Da completare prima della pubblicazione commerciale.</strong>
        Mancano i dati identificativi del venditore. Questa è una bozza tecnica,
        non una consulenza legale.
      </p>
    </div>
  );
}

function Privacy({ config }: { config: PublicConfig }) {
  const company = seller(config);
  return (
    <>
      <h1>Privacy, senza parole minuscole.</h1>
      <p className="legal-lead">
        Qui spieghiamo in modo diretto quali dati servono, perché e per quanto
        tempo. Ultimo aggiornamento: 6 agosto 2026.
      </p>
      <DraftWarning ready={config.legalReady} />
      <h2>1. Chi tratta i dati</h2>
      <p>
        Il titolare del trattamento è <strong>{company.name}</strong>, con sede in{" "}
        {company.address}, P.IVA/C.F. {company.vat}. Per richieste privacy: {" "}
        <a href={`mailto:${config.supportEmail}`}>{config.supportEmail}</a>.
      </p>
      <h2>2. Dati e finalità</h2>
      <ul>
        <li>
          <strong>Profilo e portafoglio:</strong> email, password protetta tramite
          derivazione crittografica, identificativi di sessione e saldo dei crediti
          permettono di ritrovare i VAFFA su più dispositivi. Base giuridica:
          esecuzione del servizio e legittimo interesse alla sicurezza.
        </li>
        <li>
          <strong>Testo inserito:</strong> una breve descrizione libera della
          situazione viene elaborata per generare il messaggio. Evita nomi completi, numeri,
          indirizzi e altri dati personali. Base giuridica: esecuzione della
          richiesta dell’utente.
        </li>
        <li>
          <strong>Pagamenti:</strong> Stripe tratta i dati necessari al checkout e
          alla ricevuta. Vaffangoogle conserva l’identificativo della transazione,
          il pacchetto e l’accredito, non i dati completi della carta. Base
          giuridica: contratto e obblighi fiscali.
        </li>
        <li>
          <strong>Sicurezza:</strong> indirizzo IP e log tecnici possono essere
          trattati dal servizio di hosting per prevenire abusi e incidenti.
        </li>
        <li>
          <strong>Assistenza e amministrazione:</strong> gli accrediti manuali
          conservano amministratore, profilo destinatario, quantità, motivo e data,
          così da prevenire errori e abusi. Non includono il testo delle battute.
        </li>
      </ul>
      <h2>3. Intelligenza artificiale e voce</h2>
      <p>
        Il testo necessario alla richiesta viene inviato alle API OpenAI per la
        generazione e la resa vocale. La voce è generata
        dall’IA e non appartiene a una persona reale. Prima del lancio il titolare deve
        verificare accordi sul trattamento, regione dei dati e impostazioni di
        conservazione applicabili al proprio account OpenAI.
      </p>
      <h2>4. Conservazione e destinatari</h2>
      <p>
        Il testo della richiesta non viene salvato nel database dell’app. Il file
        MP3 resta sul server fino a 24 ore, poi viene eliminato automaticamente.
        Il registro visibile nel profilo conserva per 30 giorni soltanto data, tipo
        di operazione e variazione dei crediti, mai il contenuto della battuta. Il
        registro degli accrediti amministrativi viene conservato fino a 12 mesi. Il
        profilo, il portafoglio e i riferimenti di pagamento restano per
        l’erogazione dei crediti e per i periodi imposti dalla normativa contabile.
        Possibili destinatari: hosting, OpenAI, Stripe e consulenti strettamente necessari.
      </p>
      <h2>5. Condivisione</h2>
      <p>
        Vaffangoogle non invia messaggi da solo. Quando apri il menu di
        condivisione dell’MP3, sei tu ad avviare l’azione e si applicano anche le
        informative del servizio scelto. Il file contiene metadati con il nome
        e il sito di Vaffangoogle.
      </p>
      <h2>6. I tuoi diritti</h2>
      <p>
        Puoi chiedere accesso, rettifica, cancellazione, limitazione, portabilità o
        opposizione quando applicabili, scrivendo all’assistenza. Puoi inoltre
        proporre reclamo al Garante per la protezione dei dati personali. Potremo
        chiederti elementi ragionevoli per identificare il profilo o la ricevuta
        interessata.
      </p>
      <h2>7. Minori e modifiche</h2>
      <p>
        Il servizio non è destinato a minori di 14 anni. Le modifiche sostanziali
        saranno segnalate sul sito con una nuova data di aggiornamento.
      </p>
    </>
  );
}

function Terms({ config }: { config: PublicConfig }) {
  const company = seller(config);
  return (
    <>
      <h1>Termini del gioco.</h1>
      <p className="legal-lead">
        Regole semplici: compri crediti, crei una battuta, decidi tu se condividerla.
        Ultimo aggiornamento: 6 agosto 2026.
      </p>
      <DraftWarning ready={config.legalReady} />
      <h2>1. Fornitore e servizio</h2>
      <p>
        Vaffangoogle è fornito da <strong>{company.name}</strong>, {company.address},
        P.IVA/C.F. {company.vat}. Contatti: {config.supportEmail}. Il servizio genera
        testi e, quando disponibile, audio interpretati dall’IA a scopo
        esclusivamente ludico.
      </p>
      <h2>2. Portafoglio e crediti</h2>
      <p>
        Un VAFFA consente una generazione. I crediti sono digitali, non hanno valore
        monetario, non producono interessi e non sono trasferibili. Gli acquisti
        richiedono un profilo e il saldo resta associato a quell’account, così può
        essere ritrovato su più dispositivi. Conserva comunque la ricevuta Stripe.
        I prezzi e il numero di crediti sono mostrati prima del checkout.
      </p>
      <h2>3. Pagamento e disponibilità immediata</h2>
      <p>
        Il pagamento avviene su Stripe. I crediti vengono accreditati dopo la
        conferma del pagamento. Prima del checkout chiediamo separatamente il
        consenso alla messa a disposizione immediata; dopo l’inizio dell’utilizzo,
        il diritto di recesso può cessare nei casi e nei limiti previsti dalla
        normativa sui contenuti o servizi digitali. I diritti inderogabili del
        consumatore restano sempre salvi.
      </p>
      <h2>4. Rimborsi e problemi tecnici</h2>
      <p>
        Se una generazione fallisce prima di produrre il risultato, il credito viene
        restituito automaticamente. Per addebiti duplicati, mancati accrediti o
        richieste di rimborso scrivi a {config.supportEmail} allegando l’email della
        ricevuta e l’identificativo del pagamento. Nulla in questi Termini limita
        garanzie o rimedi previsti dalla legge.
      </p>
      <h2>5. Uso accettabile</h2>
      <p>
        Non usare il servizio per minacce, molestie ripetute, odio, diffamazione,
        doxxing, ricatti, sessualizzazione, impersonificazione o attacchi a persone
        vulnerabili. Non inserire dati personali non necessari. Possiamo bloccare
        richieste o accessi in caso di abuso o rischio per terzi.
      </p>
      <h2>6. Condivisione e responsabilità</h2>
      <p>
        Nessun messaggio parte automaticamente o in anonimato. Sei responsabile di
        ascoltare il risultato, valutarne il contesto e scegliere se condividerlo.
        L’output dell’IA può essere impreciso o inopportuno: non usarlo per decisioni
        importanti, accuse o comunicazioni professionali.
      </p>
      <h2>7. Disponibilità, modifiche e legge</h2>
      <p>
        Possiamo aggiornare modelli, prezzi per nuovi acquisti e funzionalità,
        comunicando le modifiche materiali. Per i consumatori si applicano la legge
        italiana e le tutele del foro inderogabile del luogo di residenza. Prima
        della pubblicazione il professionista incaricato dovrà valutare anche la
        disciplina ADR/ODR applicabile al caso concreto.
      </p>
    </>
  );
}

function Cookies({ config }: { config: PublicConfig }) {
  return (
    <>
      <h1>Cookie tecnici. Solo quelli utili.</h1>
      <p className="legal-lead">
        Niente profilazione, pubblicità o pixel nascosti nella versione base.
      </p>
      <h2>I cookie tecnici</h2>
      <p>
        <code>vg_wallet</code> è un cookie tecnico HTTP-only che contiene un
        identificativo casuale per il saldo prima dell’accesso e dura fino a 12 mesi.
        <code>vg_session</code> mantiene aperto il profilo per 30 giorni. Entrambi
        sono indispensabili per il servizio e non vengono letti da JavaScript.
      </p>
      <h2>Memoria locale</h2>
      <p>
        <code>vg_cookie_notice</code> ricorda soltanto che hai visto l’avviso sui
        cookie. Non contiene un profilo e non viene inviato al server.
      </p>
      <h2>Stripe e servizi esterni</h2>
      <p>
        Se scegli di acquistare crediti, vieni reindirizzato al checkout Stripe,
        che può usare propri cookie secondo la sua informativa. WhatsApp e altri
        canali vengono aperti solo su tua richiesta e applicano regole proprie.
      </p>
      <h2>Come cancellarli</h2>
      <p>
        Puoi rimuoverli dalle impostazioni del browser. Se hai creato un profilo,
        potrai accedere di nuovo senza perdere i crediti. Per assistenza scrivi a
        {config.supportEmail} e conserva le ricevute di pagamento.
      </p>
    </>
  );
}

function Rules() {
  return (
    <>
      <h1>Le regole del gioco.</h1>
      <p className="legal-lead">
        Il bersaglio è il fastidio. La persona resta fuori dal mirino.
      </p>
      <div className="rules-grid">
        <article><b>01</b><h2>Fatti, non etichette</h2><p>“Hai lasciato i piatti” funziona. “Sei…” quasi mai.</p></article>
        <article><b>02</b><h2>Niente dati privati</h2><p>Usa ruoli generici: coinquilino, collega, amico. Non numeri o indirizzi.</p></article>
        <article><b>03</b><h2>Mai anonimo</h2><p>Se condividi, lo fai dal tuo dispositivo e dal tuo account.</p></article>
        <article><b>04</b><h2>Ascolta prima</h2><p>Il contesto lo conosci tu. Se punge troppo, non inviarlo.</p></article>
        <article><b>05</b><h2>Una volta basta</h2><p>Un sorriso è il punto. Ripetere contro chi non gradisce diventa molestia.</p></article>
        <article><b>06</b><h2>Non è un arbitro</h2><p>L’IA non decide chi ha ragione e non va usata per accuse reali.</p></article>
      </div>
      <div className="rules-oath">
        <ShieldCheck />
        <p>
          <strong>Il patto Vaffangoogle</strong>
          “Mi sfogo, faccio sorridere, poi torno a parlare da persona civile.”
        </p>
      </div>
    </>
  );
}

function Support({ config }: { config: PublicConfig }) {
  return (
    <>
      <h1>Ci siamo. Senza robot telefonici.</h1>
      <p className="legal-lead">
        Per pagamenti, crediti o audio capricciosi, scrivi a Free Future.
      </p>
      <a className="support-card" href={config.supportUrl}>
        <Mail />
        <span><small>Assistenza Free Future</small><strong>Apri una richiesta</strong></span>
      </a>
      <p className="support-email">
        Oppure scrivi a <a href={`mailto:${config.supportEmail}`}>{config.supportEmail}</a>.
      </p>
      <h2>Cosa includere</h2>
      <ul>
        <li>Una descrizione breve del problema.</li>
        <li>Data e ora approssimative.</li>
        <li>Per i pagamenti, l’identificativo della ricevuta Stripe.</li>
        <li>Non inviare numeri completi di carta, password o chiavi API.</li>
      </ul>
      <h2>Tempi</h2>
      <p>
        Obiettivo di risposta: entro 2 giorni lavorativi. È un obiettivo di servizio,
        non una garanzia contrattuale. Le urgenze reali non vanno affidate a questa
        app.
      </p>
    </>
  );
}

export function LegalPage({ kind, config }: { kind: LegalKind; config: PublicConfig }) {
  return (
    <main className={`legal-page legal-page--${kind}`}>
      <a className="back-link" href="/"><ArrowLeft /> Torna al generatore</a>
      <article className="legal-document">
        {kind === "privacy" ? <Privacy config={config} /> : null}
        {kind === "termini" ? <Terms config={config} /> : null}
        {kind === "cookie" ? <Cookies config={config} /> : null}
        {kind === "regole" ? <Rules /> : null}
        {kind === "assistenza" ? <Support config={config} /> : null}
      </article>
    </main>
  );
}
